// components/PhoneInput.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Input } from "@heroui/react";

export const countryCodes = [
    { code: '+32', country: 'Belgium' },
    { code: '+31', country: 'Netherlands' },
    { code: '+33', country: 'France' },
    { code: '+34', country: 'Spain' },
    { code: '+35', country: 'Italy' },
    { code: '+36', country: 'Hungary' },
    { code: '+37', country: 'Poland' },
    { code: '+38', country: 'Czech Republic' },
    { code: '+39', country: 'Greece' },
    { code: '+40', country: 'Romania' },
    // Add more country codes as needed
];

interface PhoneInputProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    editMode: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = React.memo(({ value, onChange, onBlur, editMode }) => {
    const [countryCode, setCountryCode] = useState('+32');
    const [phoneNumber, setPhoneNumber] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!value) {
            setCountryCode('+32');
            setPhoneNumber('');
        } else {
            const matchedCountry = countryCodes.find(country => value.startsWith(country.code));
            if (matchedCountry) {
                setCountryCode(matchedCountry.code);
                setPhoneNumber(value.slice(matchedCountry.code.length).replace(/^0+/, ''));
            } else {
                setPhoneNumber(value.replace(/^0+/, ''));
            }
        }
    }, [value]);

    const handleCountryCodeChange = useCallback((code: string) => {
        setCountryCode(code);
        const newPhoneNumber = phoneNumber.replace(/^0+/, '');
        onChange(`${code}${newPhoneNumber}`);
        inputRef.current?.focus();
    }, [phoneNumber, onChange]);

    const handlePhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newPhoneNumber = e.target.value.replace(/^0+/, '');
        setPhoneNumber(newPhoneNumber);
        onChange(`${countryCode}${newPhoneNumber}`);
    }, [countryCode, onChange]);

    if (!editMode) {
        return (<div
            className={`flex flex-wrap items-center justify-between p-2.5 text-sm bg-primary-moss font-bold !important`}
        >
            <span>GSM nummer</span>
            <a href={"tel:" + value} className="font-medium text-moss-primary">
                {value !== null && value !== undefined ? value : "Niet ingevuld"}
            </a>
        </div>)
    }

    return (
        <div className="flex">
            <Dropdown>
                <DropdownTrigger>
                    <Button>{countryCode}</Button>
                </DropdownTrigger>
                <DropdownMenu>
                    {countryCodes.map((country) => (
                        <DropdownItem key={country.code} onClick={() => handleCountryCodeChange(country.code)}>
                            {country.code} ({country.country})
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </Dropdown>
            <Input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                onBlur={onBlur}
                placeholder="GSM number"
                ref={inputRef}
            />
        </div>
    );
});

export default PhoneInput;